import { CountdownTimer, MUIIcon, ProgressIndicator, useCalcRemainingTime } from "../../../../widgets";
import { FC, useCallback, useState } from "react";
import { IServiceRequest, ServiceRequestStatus } from "../models";

import { ServiceReqHelper } from "../helpers/helper";
import { ServiceRequestResend } from "./ServiceRequestResend";
import { addMinutes } from "../../timeline/helper";
import { useInterval } from "react-use";
import { useServiceRequestContext } from "../service-request";
import { useGetServiceRequestGatePassId } from "../helpers";

interface IServiceRequestActionsProps {
    isSendingForApproval?: boolean,
    onCancel: (requestId: string) => void
    onReOpen: (requestId: string) => void
    onExtended: (requestId: string, extendGPId: string) => void
}

export const ServiceRequestActions: FC<IServiceRequestActionsProps> = ({ isSendingForApproval, onCancel, onReOpen, onExtended }) => {
    const { result, refetch, updateServiceRequest, updateExtendServiceRequest } = useServiceRequestContext()
    const { serviceRequest, service } = result || {}
    const { gpOut, gpIn } = useGetServiceRequestGatePassId(serviceRequest)
    const endTime = addMinutes(serviceRequest?.endTime, service?.otpTimer)
    const { isFuture } = useCalcRemainingTime({ endTime });
    const [cancelling, setCancelling] = useState<boolean>(false)
    const [reopening, setReopening] = useState<boolean>(false);
    const [extend, setExtend] = useState<boolean>(false);

    useInterval(() => {
        if (serviceRequest?.statusCode !== ServiceRequestStatus.Submitted && serviceRequest?.statusCode !== ServiceRequestStatus.AutoApproved && serviceRequest?.statusCode !== ServiceRequestStatus.Approved)
            return;

        refetch()
    }, 5000)

    const resendButtons = (serviceRequest: IServiceRequest) => {
        if (!ServiceReqHelper.isCancellable(serviceRequest?.statusCode) || !serviceRequest.endTime || isFuture !== false)
            return <></>

        if ((service?.maxApprovalAttempts && serviceRequest.counter) &&
            service.maxApprovalAttempts <= serviceRequest.counter) {
            return <></>
        }

        return <>
            <ServiceRequestResend serviceRequest={serviceRequest} />
        </>
    }

    const countdownTimer = useCallback((endTime?: Date) => {
        if (!endTime || !service?.otpTimer || service?.otpTimer <= 0 || isFuture !== true ||
            !ServiceReqHelper.isCancellable(serviceRequest?.statusCode))
            return <></>

        return <>{
            serviceRequest?.statusCode === ServiceRequestStatus.Submitted &&
            <CountdownTimer endTime={endTime} label='Waiting for Approval' />
        }
        </>

        // eslint-disable-next-line
    }, [isFuture, service, serviceRequest])

    const handleCancel = () => {
        if (!serviceRequest) return;

        setCancelling(true)
        updateServiceRequest({
            ...serviceRequest,
            statusCode: ServiceRequestStatus.Cancelled
        })
            .then(() => onCancel(serviceRequest.id))
            .finally(() => setCancelling(false))
    }

    const cancellationButton = () => {
        if (!ServiceReqHelper.isCancellable(serviceRequest?.statusCode) || serviceRequest?.allowCancellation === false)
            return <></>

        return <>
            <button
                disabled={cancelling}
                type="button" onClick={handleCancel} className='btn btn-sm btn-danger py-2 align-self-center'>
                {!cancelling && <>
                    <MUIIcon iconName={"CancelOutlined"} className="me-1" />
                    Cancel the request
                </>}
                {cancelling && <>
                    <ProgressIndicator show={true} message='Please wait...' />
                </>}
            </button>
        </>
    }

    const handleReOpen = () => {
        if (!serviceRequest) return;

        setReopening(true);
        updateServiceRequest({
            ...serviceRequest,
            statusCode: ServiceRequestStatus.ReOpen,
        })
            .then(() => onReOpen(serviceRequest.id))
            .finally(() => setReopening(false));
    };

    const reOpenButton = () => {
        if (serviceRequest?.statusCode !== 365970002 || serviceRequest?.allowReOpen !== true)
            return <></>;
        return (
            <>
                <button
                    disabled={reopening}
                    type="button"
                    onClick={handleReOpen}
                    className="btn btn-sm btn-warning py-2 align-self-center"
                >
                    {!reopening && (
                        <>
                            <MUIIcon iconName={"RedoOutlined"} className="me-1" />
                            ReOpen the request
                        </>
                    )}
                    {reopening && <ProgressIndicator show={true} message="Please wait..." />}
                </button>
            </>
        );
    };

    const handleExtended = () => {
        if (!serviceRequest) return;

        setExtend(true);
        updateExtendServiceRequest(serviceRequest.id)
            .then((data) => {
                onExtended(serviceRequest.id, data)
            })
            .finally(() => setExtend(false));
    };

    const extendedButton = () => {
        if (gpOut !== true || gpIn !== false)
            return <></>;
        return (
            <>
                <button
                    disabled={extend}
                    type="button"
                    onClick={handleExtended}
                    className="btn btn-sm btn-warning py-2 align-self-center"
                >
                    {!extend && (
                        <>
                            <MUIIcon iconName={"ExtensionOutlined"} className="me-1" />
                            Extend the request
                        </>
                    )}
                    {extend && <ProgressIndicator show={true} message="Please wait..." />}
                </button>
            </>
        );
    };

    if (!serviceRequest)
        return <></>

    if (!serviceRequest.endTime && serviceRequest?.statusCode === ServiceRequestStatus.Submitted && isSendingForApproval)
        return <ProgressIndicator className="my-3" show={true} message='Sending approval request...' />

    return <div className='my-3'>
        <div className="d-flex gap-3">
            {countdownTimer(endTime)}
            <div className="flex-fill" />
            <div className="d-flex gap-3">
                {cancellationButton()}
                {reOpenButton()}
                {resendButtons(serviceRequest)}
                {extendedButton()}
            </div>
        </div>
    </div>
}